<template>
	<div id="item-plan">
		<!-- Filter Card -->
		<v-card flat color="transparent">
			<!-- Filter inputs -->
			<v-row dense class="ma-0 px-4 pt-2">
				<!-- From date -->
				<v-col cols="6">
					<v-dialog v-model="fromDialog" max-width="290px">
						<template v-slot:activator="{ on, attrs }">
							<v-text-field
								v-bind="attrs"
								v-on="on"
								:value="fromDate"
								:label="$t('ItemPlan.from-date')"
								readonly
								dense
								hide-details
								outlined
								prepend-inner-icon="mdi-calendar-start"
							/>
						</template>
						<v-date-picker
							v-model="fromDate"
							:max="toDate || undefined"
							@input="fromDialog = false"
						/>
					</v-dialog>
				</v-col>
				<!-- To date -->
				<v-col cols="6">
					<v-dialog v-model="toDialog" max-width="290px">
						<template v-slot:activator="{ on, attrs }">
							<v-text-field
								v-bind="attrs"
								v-on="on"
								:value="toDate"
								:label="$t('ItemPlan.to-date')"
								readonly
								dense
								hide-details
								outlined
								prepend-inner-icon="mdi-calendar-end"
							/>
						</template>
						<v-date-picker
							v-model="toDate"
							:min="fromDate || undefined"
							@input="toDialog = false"
						/>
					</v-dialog>
				</v-col>
				<!-- Material name search -->
				<v-col cols="12">
					<v-text-field
						v-model="filterQuery"
						:label="$T('inputs.name')"
						dense
						hide-details
						outlined
						clearable
						prepend-inner-icon="mdi-magnify"
					/>
				</v-col>
				<!-- Specialty -->
				<v-col cols="6">
					<v-autocomplete
						v-model="filterSpecialtyId"
						:items="specialties"
						item-text="name"
						item-value="id"
						:label="$T('inputs.specialty')"
						dense
						hide-details
						outlined
						clearable
					/>
				</v-col>
				<!-- Workload -->
				<v-col cols="6">
					<v-autocomplete
						v-model="filterWorkloadId"
						:items="workloads"
						item-text="name"
						item-value="id"
						:label="$T('inputs.workload')"
						dense
						hide-details
						outlined
						clearable
					/>
				</v-col>
				<!-- Entity / Customer -->
				<v-col cols="12">
					<v-autocomplete
						v-model="filterEntityId"
						:items="entities"
						item-text="name"
						item-value="id"
						:label="$T('inputs.customer-name')"
						dense
						hide-details
						outlined
						clearable
					/>
				</v-col>
			</v-row>

			<!-- Search & Reset buttons -->
			<v-card-actions class="px-4 pb-3">
				<v-btn
					color="primary"
					small
					@click="search"
				>
					<v-icon small class="me-1">mdi-magnify</v-icon>
					{{ $t('ItemPlan.search') }}
				</v-btn>
				<v-btn
					outlined
					color="grey darken-1"
					small
					@click="reset"
				>
					<v-icon small class="me-1">mdi-refresh</v-icon>
					{{ $t('ItemPlan.reset') }}
				</v-btn>
				<!-- <v-spacer /> -->
			</v-card-actions>
		</v-card>

		<loader v-if="loading" />
		<v-container fluid v-else>
			<v-expansion-panels v-if="itemPlanMaterials.length">
				<v-expansion-panel
					v-for="specialtyItem in itemPlanMaterials"
					:key="specialtyItem.id"
				>
					<v-expansion-panel-header class="px-3">
						<div class="me-2 flex-grow-0">
							<v-icon>mdi-pill</v-icon>
						</div>
						<div>{{ specialtyItem.specialtyName }}</div>
					</v-expansion-panel-header>
					<v-expansion-panel-content class="px-3">
						<!-- Material Cards -->
						<v-row v-if="specialtyItem.materials.length" class="mb-3">
							<v-col
								v-for="material in specialtyItem.materials"
								:key="material.id"
								cols="12"
								class="py-2"
							>
								<v-card flat border>
									<v-card-text class="d-flex justify-space-between pa-4">
										<!-- Material Name and Form -->
										<div class="d-flex align-center mb-2">
											<span class="primary--text font-weight-bold text-body-1">
												{{ material.materialName || getMaterialById(material.materialId)?.name }}
											</span>
											<span class="ms-2 text-body-2">
												({{ getMaterialFormById(getMaterialById(material.materialId)?.materialFormId)?.name || '' }})
											</span>
										</div>

										<span class="ms-2">{{ $options.filters.formatNumber(material.amount, 3) }}</span>
									</v-card-text>
								</v-card>
							</v-col>
						</v-row>

						<!-- No Materials Message -->
						<div v-else class="text-center pa-4 grey--text text-body-2">
							{{ $t('ItemPlan.no-items-to-show') }}
						</div>
					</v-expansion-panel-content>
				</v-expansion-panel>
			</v-expansion-panels>

			<!-- Empty State -->
			<div
				v-else
				class="d-flex flex-column justify-center align-center"
				style="height: 45vh"
			>
				<v-icon x-large class="mb-3">mdi-pill</v-icon>
				<span class="d-flex flex-column justify-center align-center grey--text text-body-1">
					{{ $t('ItemPlan.no-items-to-show') }}
				</span>
			</div>
		</v-container>
	</div>
</template>

<script>
import moment from 'moment'
import { mapGetters, mapState } from 'vuex'
import Loader from '@/components/Loader.vue'

export default {
	name: 'ItemPlan',

	components: { Loader },

	data() {
		return {
			moment,
			loading: false,
			fromDialog: false,
			toDialog: false,
			fromDate: moment().startOf('month').format('YYYY-MM-DD'),
			toDate: moment().endOf('month').format('YYYY-MM-DD'),

			filterSpecialtyId: null,
			filterWorkloadId: null,
			filterEntityId: null,
			filterQuery: null,
		}
	},

	computed: {
		...mapState({
			specialties: state => state.specialties.specialties,
			rawItemPlanMaterials: state => state.materials.specialtyMaterials,
			workloads: state => state.workloads.workloads,
			materials: state => state.materials.materials,
			materialForms: state => state.materialForms.materialForms,
			entities: state => state.entities.entities,
		}),

		...mapGetters({
			getSpecialtyById: 'specialties/getSpecialtyById',
			getWorkloadById: 'workloads/getWorkloadById',
			getMaterialById: 'materials/getMaterialById',
			getMaterialFormById: 'materialForms/getMaterialFormById'
		}),

		itemPlanMaterials() {
			if (!this.rawItemPlanMaterials || !this.rawItemPlanMaterials.length) {
				return []
			}

			const grouped = {}

			this.rawItemPlanMaterials.forEach(specialtyGroup => {
				const specialtyId = specialtyGroup.specialtyId
				if (!grouped[specialtyId]) {
					grouped[specialtyId] = {
						specialtyId,
						id: `specialty-${specialtyId}`,
						specialtyName: specialtyGroup.specialtyName || this.getSpecialtyById(specialtyId)?.name || 'Unknown',
						materials: []
					}
				}

				// Add workload materials if they exist
				if (specialtyGroup.workloads && specialtyGroup.workloads.length) {
					specialtyGroup.workloads.forEach(workload => {
						workload.materials.forEach(material => {
							grouped[specialtyId].materials.push({
								...material,
								workloadId: workload.workloadId,
								workloadName: workload.workloadName
							})
						})
					})
				}

				// Add direct materials
				if (specialtyGroup.materials && specialtyGroup.materials.length) {
					grouped[specialtyId].materials.push(...specialtyGroup.materials)
				}
			})

			return Object.values(grouped)
		}
	},

	methods: {
		search() {
			this.loading = true
			this.$store.dispatch('materials/fetchAllForSpecialtyVisits', {
				fromDate: this.fromDate,
				toDate: this.toDate,
				specialtyId: this.filterSpecialtyId || undefined,
				workloadId: this.filterWorkloadId || undefined,
				entityId: this.filterEntityId || undefined,
				query: this.filterQuery || undefined,
			}).finally(() => {
				this.loading = false
			})
		},

		reset() {
			this.filterSpecialtyId = null
			this.filterWorkloadId = null
			this.filterEntityId = null
			this.filterQuery = null
			this.search()
		},
	},

	created() {
		this.loading = true
		Promise.all([
			!this.specialties.length ? this.$store.dispatch('specialties/fetchAll') : null,
			!this.workloads.length ? this.$store.dispatch('workloads/fetchAll') : null,
			!this.materialForms.length ? this.$store.dispatch('materialForms/fetchAll') : null,
			this.$store.dispatch('materials/fetchAll', {}),
			!this.entities.length ? this.$store.dispatch('entities/fetchAll', {}) : null,
			!this.itemPlanMaterials.length
				? this.$store.dispatch('materials/fetchAllForSpecialtyVisits', {
					fromDate: moment().startOf('month').format('YYYY-MM-DD'),
					toDate: moment().endOf('month').format('YYYY-MM-DD'),
				})
				: null,
		]).finally(() => {
			this.loading = false
		})
	}
}
</script>

<style lang="scss" scoped>
#item-plan {
	.v-expansion-panel-content__wrap {
		padding: 0
	}

	.v-card {
		border: 1px solid #e0e0e0
	}
}
</style>
